import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 20000-20999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const projectsErrors = {
  // 20xxx is a fresh block for the Projects module (packages/server/projects/) — split off
  // SOMETHING_WENT_WRONG. The dominant miscategorization in this module was `gate.errorMessage`
  // (project.authz.ts's requireProjectPermission/requireProjectMembership deny path, always the
  // literal `noPermissionMsg`) forwarded with a hardcoded SOMETHING_WENT_WRONG instead of the
  // already-established `INSUFFICIENT_PERMISSION` — those sites reuse that existing code, not a new
  // one. The codes below are for the remaining genuine, nameable business rules.
  //
  // `insertProjectsData`/`updateProjectsData`: one or more mandatory custom fields were left empty.
  PROJECT_MANDATORY_CUSTOM_FIELDS_EMPTY: {
    errorCode: 20001,
    errorMessage: t("One or more mandatory custom fields are empty. Fill them all before saving."),
    isDynamic: false,
  },
  // `insertProjectsData`: the portal has no default currency configured, so a new project can't
  // resolve one.
  PROJECT_NO_CURRENCY_CONFIGURED: {
    errorCode: 20002,
    errorMessage: t("No currency is configured for this portal. Add a currency before creating a project."),
    isDynamic: false,
  },
  // `insertProjectsData`: the creating user's partial seat only allows membership in one project,
  // and they already have one.
  PROJECT_SEAT_LIMIT_REACHED: {
    errorCode: 20003,
    errorMessage: t("Project could not be created. Note: Your seat does not allow you to perform this action."),
    isDynamic: false,
  },
  // `updateProjectRole`/`deleteProjectRole`: the named project role id no longer resolves (stale
  // client state, or already deleted).
  PROJECT_ROLE_NOT_FOUND: {
    errorCode: 20004,
    errorMessage: t("That project role no longer exists."),
    isDynamic: false,
  },
  // `updateProjectRole`/`deleteProjectRole`/`changeProjectRolePermission`: a default (system) role
  // can't be modified or deleted. 3 call sites, same protection rule.
  PROJECT_DEFAULT_ROLE_PROTECTED: {
    errorCode: 20005,
    errorMessage: t("A default project role cannot be modified or deleted"),
    isDynamic: false,
  },
  // `deleteProjectRole`: the role is still assigned to at least one user.
  PROJECT_ROLE_IN_USE: {
    errorCode: 20006,
    errorMessage: t("Role cannot be deleted as it is assigned to at least one user"),
    isDynamic: false,
  },
  // `getModelFileFormTemplates`: no form template has been assigned to this project/resourceType or
  // file. 2 call sites, same concept.
  PROJECT_FORM_TEMPLATE_NOT_ASSIGNED: {
    errorCode: 20007,
    errorMessage: t("No template has been assigned to this project or file"),
    isDynamic: false,
  },
  // 20008+ is the projects module's RESOURCE_NOT_FOUND (9003) split. Nothing above came off that code.
  //
  // A `projects` row could not be resolved. 7 call sites. Three wordings were unified here with the
  // owner's sign-off: "Project not found", "Project not found." (trailing period), and
  // "Project does not exists with given projectId" (grammatically broken, and it leaked the parameter
  // name to the user). Two of those sites are `requireProjectMembership` DENY paths that deliberately
  // answer with this not-found payload rather than a permission error — that identical answer is what
  // stops the endpoint becoming a cross-portal project-existence oracle, so never move them to
  // INSUFFICIENT_PERMISSION.
  PROJECT_NOT_FOUND: {
    errorCode: 20008,
    errorMessage: t("Project not found"),
    isDynamic: false,
  },
  // A `Locations` row could not be resolved. 4 call sites, including the one that resolves a proposed
  // PARENT location — same collection, same wording, so deliberately not a separate code.
  PROJECT_LOCATION_NOT_FOUND: {
    errorCode: 20009,
    errorMessage: t("Location not found"),
    isDynamic: false,
  },
  // A `Teams` row could not be resolved.
  PROJECT_TEAM_NOT_FOUND: {
    errorCode: 20010,
    errorMessage: t("Team not found"),
    isDynamic: false,
  },
  // `insertWBS`: the parent `projectWBS` row named by the client does not exist.
  PROJECT_PARENT_WBS_NOT_FOUND: {
    errorCode: 20011,
    errorMessage: t("Parent WBS not found"),
    isDynamic: false,
  },
  // `changeUserRoleFromProject`: the target user has no entry in the project's embedded `users[]`
  // membership array. A membership miss, not a missing account — hence not ACCOUNT_NOT_FOUND.
  PROJECT_USER_NOT_IN_PROJECT: {
    errorCode: 20012,
    errorMessage: t("User not found in project"),
    isDynamic: false,
  },
  // `insertProjectRole`: the `copyRole` id names no role in this project.
  PROJECT_ROLE_TO_COPY_NOT_FOUND: {
    errorCode: 20013,
    errorMessage: t("The role to copy does not exist in this project."),
    isDynamic: false,
  },
  // `insertProjectsData`: the creating user's own `accounts` row could not be resolved, so there is no
  // creator stub to stamp on the project.
  PROJECT_CREATOR_ACCOUNT_NOT_FOUND: {
    errorCode: 20014,
    errorMessage: t("Your user account could not be resolved, so the project cannot be created."),
    isDynamic: false,
  },
  // `insertProjectsData`: the DEFAULT_SYSTEM_USER account could not be resolved.
  PROJECT_DEFAULT_SYSTEM_USER_NOT_FOUND: {
    errorCode: 20015,
    errorMessage: t("The default system user could not be resolved, so the project cannot be created."),
    isDynamic: false,
  },
  // `insertProjectsData`: the global default-roles constant carries no PROJECT_MANAGER role to assign.
  // A configuration defect rather than a lookup miss, but it is answered on this same path.
  PROJECT_DEFAULT_ROLES_MISCONFIGURED: {
    errorCode: 20016,
    errorMessage: t("The default project roles are misconfigured: there is no PROJECT_MANAGER role to assign."),
    isDynamic: false,
  },
  // `getModelFileFormTemplates`: the named file version does not exist on this project.
  PROJECT_FILE_VERSION_NOT_FOUND: {
    errorCode: 20017,
    errorMessage: t("File not found"),
    isDynamic: false,
  },
  // `getAllUsedFormTemplates`: the project has no form templates in use. Also returned on that
  // endpoint's permission-deny path on purpose, for the same anti-oracle reason as PROJECT_NOT_FOUND.
  PROJECT_NO_FORM_TEMPLATES_IN_USE: {
    errorCode: 20018,
    errorMessage: t("No form templates are in use on this project."),
    isDynamic: false,
  },
  // 20019-20031 completes the projects module's split. PROJECT_USERS_ADD_FAILED deliberately does
  // NOT reuse GROUP_PROJECT_USERS_ADD_FAILED (32011) despite the identical sentence — that code
  // belongs to the groups module's own flow.
  PROJECT_NO_MODEL_FILES_UPDATED: {
    errorCode: 20019,
    errorMessage: t("No model files were updated with the selected form template."),
    isDynamic: false,
  },
  PROJECT_USERS_ADD_FAILED: {
    errorCode: 20020,
    errorMessage: t("The selected user(s) could not be added to the project."),
    isDynamic: false,
  },
  PROJECT_USERS_REMOVE_FAILED: {
    errorCode: 20021,
    errorMessage: t("The selected user(s) could not be removed from the project."),
    isDynamic: false,
  },
  PROJECT_WHITELIST_EMAILS_ADD_FAILED: {
    errorCode: 20022,
    errorMessage: t("The e-mail(s) could not be added to the project whitelist."),
    isDynamic: false,
  },
  PROJECT_WHITELIST_EMAILS_REMOVE_FAILED: {
    errorCode: 20023,
    errorMessage: t("The selected e-mail(s) could not be removed from the project whitelist."),
    isDynamic: false,
  },
  PROJECT_TEAM_CREATE_FAILED: {
    errorCode: 20024,
    errorMessage: t("Failed to create the Team."),
    isDynamic: false,
  },
  PROJECT_TEAM_UPDATE_FAILED: {
    errorCode: 20025,
    errorMessage: t("Failed to update the Team."),
    isDynamic: false,
  },
  PROJECT_TEAMS_DELETE_FAILED: {
    errorCode: 20026,
    errorMessage: t("Failed to delete the selected Teams."),
    isDynamic: false,
  },
  PROJECT_LOCATION_CREATE_FAILED: {
    errorCode: 20027,
    errorMessage: t("Failed to create the Location."),
    isDynamic: false,
  },
  PROJECT_LOCATION_SELF_PARENT: {
    errorCode: 20028,
    errorMessage: t("A Location cannot be its own parent"),
    isDynamic: false,
  },
  PROJECT_LOCATION_DESCENDANT_PARENT: {
    errorCode: 20029,
    errorMessage: t("A Location cannot be moved under one of its own descendants"),
    isDynamic: false,
  },
  PROJECT_LOCATION_UPDATE_FAILED: {
    errorCode: 20030,
    errorMessage: t("Failed to update the Location."),
    isDynamic: false,
  },
  PROJECT_WBS_ITEM_CREATE_FAILED: {
    errorCode: 20031,
    errorMessage: t("Failed to create the WBS item."),
    isDynamic: false,
  },
  // Genuine permission refusals with their own sentences — a rank/role guard, not the generic gate.
  PROJECT_USER_PROMOTE_NOT_ALLOWED: {
    errorCode: 20032,
    errorMessage: t("You are not allowed to promote this user to higher rank role"),
    isDynamic: false,
  },
  PROJECT_USER_ROLE_CHANGE_NOT_ALLOWED: {
    errorCode: 20033,
    errorMessage: t("You are not allowed to change role of this user"),
    isDynamic: false,
  },
  // 20034-20039 came off NO_CHANGES_APPLIED (9005).
  PROJECT_USERS_ALREADY_PRESENT: {
    errorCode: 20034,
    errorMessage: t("User(s) already present in the selected project."),
    isDynamic: false,
  },
  PROJECT_EMAILS_ALREADY_WHITELISTED: {
    errorCode: 20035,
    errorMessage: t("Every e-mail in the request is already whitelisted on this project."),
    isDynamic: false,
  },
  PROJECT_MAIL_ADDRESS_IN_USE: {
    errorCode: 20036,
    errorMessage: t("Mail address is already in use"),
    isDynamic: false,
  },
  PROJECT_ADDRESS_UNCHANGED: {
    errorCode: 20037,
    errorMessage: t("Project not found or address unchanged"),
    isDynamic: false,
  },
  PROJECT_UPDATE_FAILED_PLAIN: {
    errorCode: 20038,
    errorMessage: t("Failed to Update!"),
    isDynamic: false,
  },
  PROJECT_WBS_ITEM_UNCHANGED: {
    errorCode: 20039,
    errorMessage: t("WBS item not found, or no field changed."),
    isDynamic: false,
  },
  PROJECT_ID_REQUIRED: {
    errorCode: 20040,
    errorMessage: t("Project Id is required"),
    isDynamic: false,
  },
  PROJECT_EMAILS_INVALID: {
    errorCode: 20041,
    errorMessage: t("Invalid email(s)"),
    isDynamic: false,
  },
  PROJECT_ID_REQUIRED_LOWER: {
    errorCode: 20042,
    errorMessage: t("projectId is required."),
    isDynamic: false,
  },
  // 20043: the add-users partial failure that was not already covered by PROJECT_USERS_ALREADY_PRESENT.
  PROJECT_USERS_ADD_NEEDS_FULL_SEAT: {
    errorCode: 20043,
    errorMessage: t("Some user(s) could not be added. Verify that they have a full seat and try again."),
    isDynamic: false,
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
